import {memo, useCallback, useContext, useMemo, useState,} from 'react'
import { notifyError } from '../../../../../utils/notifications'
import {useParams} from 'react-router-dom'
import {Flex, Form, Typography} from 'antd'
import Button from '../../../../common/Button'
import InputText from '../../../../common/InputText'
import {postRequest} from '../../../../../services/base'
import {decrypt} from '../../../../../utils/encryption'
import {NavContext} from '../../../../../context/global_context'
import {useIntl} from 'react-intl'
import useDebouncedValue from '../../../../../utils/common_function'
import {
    RESET_SELECTED_CANDIDATES,
    SET_FUNCTIONAL_DIAGRAM_DETAILS,
    useCandidateContext,
    usePageContext,
} from '../context/context'
import CandidateConnectorsTable from './CandidateConnectorsTable'
import CategoryFilterPopover from './CategoryFilterPopover'
import useTableScrollY from '../useTableScrollY'

const CandidateConnectorsSection = () => {
  const intl = useIntl()
  const { functional, vehicle } = useParams()
  const [candidateState, dispatch] = useCandidateContext()
  const [pageState] = usePageContext()
  const [, navDispatch] = useContext(NavContext)
  const [searchValue, setSearchValue] = useState('')
  const debouncedSearch = useDebouncedValue(searchValue, 500)

  const [tableAreaRef, tableHeight] = useTableScrollY([candidateState.selectedRecord])

  const filteredCandidates = useMemo(() => {
    let rows = candidateState.candidateConnectors.map((row) => {
      const mapped = candidateState.mappedConnectors.find(
        (m) => m.connector_data_id === row.id
      )
      return mapped ? { ...row, isMapped: true } : row
    })

    if (candidateState.selectedConnectorCategories?.length > 0) {
      rows = rows.filter((row) =>
        candidateState.selectedConnectorCategories.includes(row.category)
      )
    }

    if (debouncedSearch) {
      rows = rows.filter((row) =>
        `${row.name} ${row.description}`
          .toLowerCase()
          .includes(debouncedSearch.toLowerCase())
      )
    }

    return rows
  }, [
    candidateState.candidateConnectors,
    candidateState.mappedConnectors,
    candidateState.selectedConnectorCategories,
    debouncedSearch,
  ])

  const saveMapping = useCallback(
    async (mode = 'mapping') => {
      navDispatch({
        type: 'loader',
        data: {
          caption: intl.formatMessage({ id: 'functionalDiagram.savingMapping' }),
          showLoader: true,
        },
        notification: {},
      })

      const formData = new FormData()
      formData.append('vehicle', vehicle)
      formData.append('action', 'create')
      if (mode === 'mapping') {
        candidateState.selectedCandidateIds.forEach((id) =>
          formData.append('connector', id)
        )
      }

      if (candidateState.selectedRecord?.type === 'connectors') {
        candidateState.selectedCircuitPins?.forEach((row) =>
          formData.append('start_connector', row.split('**$$')[2])
        )
      }

      let res = await postRequest(
        `/api/sub_system/functional-diagram/connector/${decrypt(functional)}/?vehicle=${vehicle}`,
        formData
      )

      // Falling through dispatched `undefined` for sales_code/data/category,
      // silently wiping the mapped connectors on screen.
      if (res?.status === false) {
        notifyError(navDispatch, intl.formatMessage({ id: 'functionalDiagram.mappingUpdateError' }))
        return
      }
      dispatch({
        type: SET_FUNCTIONAL_DIAGRAM_DETAILS,
        data: {
          ...pageState.functionalDiagramDetails,
          sales_code_data: res.sales_code,
        },
        mappedConnector: res.data,
        connectorCategories: res.category,
      })

      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'functionalDiagram.mappingSuccess' }),
        },
      })
    },
    [
      functional,
      vehicle,
      candidateState.selectedCandidateIds,
      candidateState.selectedRecord,
      candidateState.selectedCircuitPins,
      pageState.functionalDiagramDetails,
      dispatch,
      navDispatch,
      intl,
    ]
  )

  const onClearCandidates = useCallback(
    () => dispatch({ type: RESET_SELECTED_CANDIDATES }),
    [dispatch]
  )

  const showSaveMappingActions =
    filteredCandidates?.length > 0 &&
    candidateState.selectedCandidateIds?.length > 0

  const showSaveStartConnectors =
    candidateState.selectedCandidateIds?.length === 0 &&
    candidateState.selectedCircuitPins?.length > 0 &&
    candidateState.selectedRecord?.type === 'connectors'

  if (!candidateState.selectedRecord) {
    return (
      <Flex
        justify="center"
        align="center"
        className="col-body col-placeholder"
      >
        <Typography.Text>
          {intl.formatMessage({ id: 'functionalDiagram.selectToStartMapping' })}
        </Typography.Text>
      </Flex>
    )
  }

  return (
    <div className="col-body" style={{ height: '100%' }}>
      <Flex gap={8}>
        <Form style={{ flex: 1 }}>
          <InputText
            type="search"
            name="search_candidate_connector"
            placeholder={intl.formatMessage({
              id: 'functionalDiagram.searchConnectors',
            })}
            isRequired={false}
            value={searchValue}
            onChange={setSearchValue}
          />
        </Form>
        <CategoryFilterPopover />
      </Flex>

      <div
        ref={tableAreaRef}
        style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
        data-testid="fd-candidates"
      >
        <CandidateConnectorsTable
          rows={filteredCandidates}
          scrollY={tableHeight}
        />
      </div>

      {(showSaveMappingActions || showSaveStartConnectors) && (
        <Flex
          gap={16}
          className="candidate-actions"
          style={{ flex: '0 0 auto', marginTop: 'auto' }}
        >
          {showSaveMappingActions ? (
            <>
              <Button
                type="primary"
                style={{ flex: 1 }}
                onClick={() => saveMapping('mapping')}
                testId="fd-save-mapping"
              >
                {intl.formatMessage({ id: 'functionalDiagram.saveMapping' })}
              </Button>
              <Button style={{ flex: 1 }} onClick={onClearCandidates} testId="fd-clear-selection">
                {intl.formatMessage({ id: 'functionalDiagram.clearSelection' })}
              </Button>
            </>
          ) : (
            <Button
              type="primary"
              style={{ flex: 1 }}
              onClick={() => saveMapping('startConnectors')}
              testId="fd-save-start"
            >
              {intl.formatMessage({
                id: 'functionalDiagram.saveStartConnectors',
              })}
            </Button>
          )}
        </Flex>
      )}
    </div>
  )
}

export default memo(CandidateConnectorsSection)
